
import { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useAuthMethods } from './useAuthMethods';
import { useSyncState } from './hooks/useSyncState';
import { toast } from 'sonner';
import type { AuthContextType } from './types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [offlineMode, setOfflineMode] = useState(!navigator.onLine);
  const syncTriggeredRef = useRef(false);
  
  const authMethods = useAuthMethods();
  const { 
    syncAllDataWithStatus, 
    refreshData, 
    syncInProgress, 
    syncError 
  } = useSyncState();

  // Keep a stable reference to the latest sync function without re-subscribing auth listeners.
  // Re-subscribing can cause us to miss the SIGNED_IN event during OAuth redirects.
  const syncFnRef = useRef(syncAllDataWithStatus);
  useEffect(() => {
    syncFnRef.current = syncAllDataWithStatus;
  }, [syncAllDataWithStatus]);

  // Listen for online/offline status
  useEffect(() => {
    const handleOnline = () => {
      console.log('App is online');
      setOfflineMode(false);
      if (user) {
        refreshData(user);
      }
    };

    const handleOffline = () => {
      console.log('App is offline');
      setOfflineMode(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [user, refreshData]);

  // Initialize auth state - DON'T wait for sync to complete
  // IMPORTANT: run once; otherwise we can miss SIGNED_IN during OAuth redirects.
  useEffect(() => {
    let mounted = true;
    
    console.log('Setting up auth state listener...');
    
    // Set up auth state change listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (!mounted) return;
        
        if (import.meta.env.DEV) console.log('Auth state changed:', event);
        
        // Always update session and user state immediately
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setLoading(false); // Stop loading immediately after auth state is known
        
        // Reset on sign out
        if (event === 'SIGNED_OUT') {
          syncTriggeredRef.current = false;
          return;
        }
        
        // Trigger background sync on sign in (don't block UI)
        if (event === 'SIGNED_IN' && !syncTriggeredRef.current && navigator.onLine) {
          syncTriggeredRef.current = true;
          // Run sync in background - don't await
          syncFnRef.current(newSession!.user.id).catch(err => {
            console.error('Background sync error:', err);
          });
          toast.success('Signed in successfully');
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (!mounted) return;
      
      if (import.meta.env.DEV) console.log('Initial session check:', initialSession?.user?.email);
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      
      // Use queueMicrotask to ensure user/session state updates are processed
      // before loading flips to false, preventing premature redirect
      queueMicrotask(() => {
        if (mounted) setLoading(false);
      });
      
      // Trigger background sync if we have a session
      if (initialSession && navigator.onLine && !syncTriggeredRef.current) {
        syncTriggeredRef.current = true;
        syncFnRef.current(initialSession.user.id).catch(err => {
          console.error('Initial sync error:', err);
        });
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const value: AuthContextType = {
    session,
    user,
    loading,
    syncComplete: !syncInProgress,
    syncInProgress,
    syncError,
    signInWithEmail: authMethods.signInWithEmail,
    signUpWithEmail: authMethods.signUpWithEmail,
    signInWithMagicLink: authMethods.signInWithMagicLink,
    signOut: authMethods.signOut,
    isAuthenticated: !!user,
    refreshData: (user) => refreshData(user),
    offlineMode
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
